import {
  EXPANDED_CARRIER_PANEL,
  type ExpandedCarrierPanel,
} from "../components/NewRequisition/types";

export const TEST_TYPE_LABELS = {
  UNITY_COMPLETE: "Unity Complete",
  CARRIER_SCREEN: "Carrier Screen",
  ANEUPLOIDY_NIPT: "Aneuploidy NIPT",
  UNITY_CONFIRM: "Unity Confirm",
} as const;

export interface TestOrderDisplay {
  primary: string;
  secondary?: string;
}

export interface TestOrderDisplayOptions {
  isCarrierScreenOrder: boolean;
  isAneuploidyOrder: boolean;
  expandedCarrierPanel?: ExpandedCarrierPanel | null;
  isFragileX: boolean;
  isAcogPlus: boolean;
  isAneuploidyOptOutFetalSex: boolean;
  isAneuploidyIncludeRhd: boolean;
  isFetalAntigen: boolean;
}

export type TestOrderTypeFlags = Pick<
  TestOrderDisplayOptions,
  "isCarrierScreenOrder" | "isAneuploidyOrder"
>;

const VALID_CARRIER_PANEL_GENE_COUNTS = new Set([5, 6, 13, 14, 113, 130, 611]);

const getCarrierPanelGeneCount = (
  expandedCarrierPanel: ExpandedCarrierPanel | null | undefined,
  isFragileX: boolean,
  isAcogPlus: boolean,
): number => {
  if (expandedCarrierPanel === EXPANDED_CARRIER_PANEL.CARRIER_113_CUSTOM) {
    return 113;
  }
  if (expandedCarrierPanel === EXPANDED_CARRIER_PANEL.CARRIER_130) {
    return 130;
  }
  if (expandedCarrierPanel === EXPANDED_CARRIER_PANEL.CARRIER_611) {
    return 611;
  }
  if (isFragileX && isAcogPlus) {
    return 14;
  }
  if (isAcogPlus) {
    return 13;
  }
  if (isFragileX) {
    return 6;
  }
  return 5;
};

const formatCarrierPanelLabel = (geneCount: number): string =>
  `${geneCount}-gene Panel`;

export const getCarrierPanelLabel = (
  expandedCarrierPanel: ExpandedCarrierPanel | null | undefined,
  isFragileX: boolean,
  isAcogPlus: boolean,
): string => {
  const geneCount = getCarrierPanelGeneCount(
    expandedCarrierPanel,
    isFragileX,
    isAcogPlus,
  );
  return formatCarrierPanelLabel(geneCount);
};

/** Converts normalized carrier panel metadata into its shared display label. */
export const getCarrierPanelDisplayLabel = (
  geneCount?: number | null,
): string | undefined => {
  if (geneCount != null && VALID_CARRIER_PANEL_GENE_COUNTS.has(geneCount)) {
    return formatCarrierPanelLabel(geneCount);
  }
  return undefined;
};

const getAneuploidySubtitle = (
  includeFetalSex: boolean,
  includeRhd: boolean,
  includeFetalAntigen: boolean,
): string | undefined => {
  const details: string[] = [];
  if (includeFetalSex) {
    details.push("Fetal Sex");
  }
  if (includeRhd) {
    details.push("Rh(D)");
  }
  if (includeFetalAntigen) {
    details.push("Fetal Antigen");
  }
  return details.length ? `w/ ${details.join(" & ")}` : undefined;
};

export const getTestOrderTypeFlags = (
  carrierResult: string,
  aneuploidyResult: string,
  carrierPanelGeneCount?: number | null,
): TestOrderTypeFlags => ({
  isCarrierScreenOrder:
    carrierResult !== "Not Ordered" ||
    (carrierPanelGeneCount != null &&
      VALID_CARRIER_PANEL_GENE_COUNTS.has(carrierPanelGeneCount)),
  isAneuploidyOrder: aneuploidyResult !== "Not Ordered",
});

const getPrimaryTestTypeLabel = ({
  isCarrierScreenOrder,
  isAneuploidyOrder,
}: TestOrderTypeFlags): string => {
  if (isCarrierScreenOrder && isAneuploidyOrder) {
    return TEST_TYPE_LABELS.UNITY_COMPLETE;
  }
  if (isCarrierScreenOrder) {
    return TEST_TYPE_LABELS.CARRIER_SCREEN;
  }
  if (isAneuploidyOrder) {
    return TEST_TYPE_LABELS.ANEUPLOIDY_NIPT;
  }
  return "";
};

const buildTestOrderDisplay = (
  orderTypeFlags: TestOrderTypeFlags,
  carrierPanel?: string,
  aneuploidySubtitle?: string,
): TestOrderDisplay => {
  const secondary = [carrierPanel, aneuploidySubtitle]
    .filter(Boolean)
    .join(", ");

  return {
    primary: getPrimaryTestTypeLabel(orderTypeFlags),
    secondary: secondary || undefined,
  };
};

const formatTestOrderDisplay = ({
  primary,
  secondary,
}: TestOrderDisplay): string => {
  if (!secondary) {
    return primary;
  }

  return `${primary} - ${secondary}`;
};

export const getTestOrderDisplay = ({
  isCarrierScreenOrder,
  isAneuploidyOrder,
  expandedCarrierPanel,
  isFragileX,
  isAcogPlus,
  isAneuploidyOptOutFetalSex,
  isAneuploidyIncludeRhd,
  isFetalAntigen,
}: TestOrderDisplayOptions): TestOrderDisplay => {
  const carrierPanel = isCarrierScreenOrder
    ? getCarrierPanelLabel(expandedCarrierPanel, isFragileX, isAcogPlus)
    : undefined;
  const aneuploidySubtitle = isAneuploidyOrder
    ? getAneuploidySubtitle(
        !isAneuploidyOptOutFetalSex,
        isAneuploidyIncludeRhd,
        isFetalAntigen,
      )
    : undefined;

  return buildTestOrderDisplay(
    { isCarrierScreenOrder, isAneuploidyOrder },
    carrierPanel,
    aneuploidySubtitle,
  );
};

const formatDetailedTestOrder = (
  orderTypeFlags: TestOrderTypeFlags,
  carrierPanelGeneCount: number | null | undefined,
  includeFetalSex: boolean,
  includeRhd: boolean,
  includeFetalAntigen: boolean,
): string => {
  const tests: string[] = [];
  if (orderTypeFlags.isCarrierScreenOrder) {
    const carrierLabel =
      carrierPanelGeneCount != null &&
      VALID_CARRIER_PANEL_GENE_COUNTS.has(carrierPanelGeneCount)
        ? `Carrier (${carrierPanelGeneCount}-gene)`
        : "Carrier";
    tests.push(carrierLabel);
  }
  if (orderTypeFlags.isAneuploidyOrder) {
    tests.push("Aneuploidy");
  }

  const addOns = orderTypeFlags.isAneuploidyOrder
    ? getAneuploidySubtitle(includeFetalSex, includeRhd, includeFetalAntigen)
    : undefined;
  return `${tests.join(" + ")}${addOns ? ` ${addOns}` : ""}`;
};

const formatInferredTestOrder = (
  orderTypeFlags: TestOrderTypeFlags | undefined,
  carrierPanelGeneCount: number | null | undefined,
  fetalAntigenPanel: string | null | undefined,
): string => {
  if (!orderTypeFlags) {
    return "";
  }

  return formatDetailedTestOrder(
    orderTypeFlags,
    carrierPanelGeneCount,
    false,
    false,
    Boolean(fetalAntigenPanel),
  );
};

export const formatTestOrderedForDisplay = (
  testOrdered?: string,
  carrierPanelGeneCount?: number | null,
  fetalAntigenPanel?: string | null,
  orderTypeFlags?: TestOrderTypeFlags,
): string => {
  const displayValue = testOrdered || "";
  if (!displayValue) {
    return formatInferredTestOrder(
      orderTypeFlags,
      carrierPanelGeneCount,
      fetalAntigenPanel,
    );
  }

  if (displayValue === "Rh(D) Only (Aneuploidy Opted Out)") {
    return "Rh(D) Only";
  }

  if (displayValue === "Fetal Antigen Only (Aneuploidy Opted Out)") {
    return "Fetal Antigen Only";
  }

  const includesCarrier = displayValue.includes("Carrier Screen");
  const includesAneuploidy = displayValue.includes("Aneuploidy");

  if (!includesCarrier && !includesAneuploidy) {
    return displayValue;
  }

  const storedOrderTypeFlags = {
    isCarrierScreenOrder: includesCarrier,
    isAneuploidyOrder: includesAneuploidy,
  };
  return formatDetailedTestOrder(
    storedOrderTypeFlags,
    carrierPanelGeneCount,
    displayValue.includes("Fetal Sex"),
    displayValue.includes("Rh(D)"),
    Boolean(fetalAntigenPanel),
  );
};
